import { InfoOutline } from "@mui/icons-material";
import { breakpointsTailwind, useMediaQuery } from "@react-hooks-library/core";
import { cva, type VariantProps } from "class-variance-authority";
import { TriangleAlert } from "lucide-react";
import * as React from "react";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";

import { RenderIf } from "../../common/RenderIf";
import { cn } from "../../lib/utils";

const expandableInfoBubbleVariants = cva("flex items-center rounded-full", {
	variants: {
		variant: {
			default: "bg-blue-300/50",
			stealth: "bg-[#283646]/80",
			alert: "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/90 backdrop-blur dark:text-white",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

const expandableInfoBubbleTriggerVariants = cva(
	"size-10 flex-shrink-0 flex items-center justify-center rounded-full",
	{
		variants: {
			variant: {
				default: "bg-blue-300 hover:bg-stealth-gradient-blue-light/50",
				stealth: "bg-stealth-border hover:bg-stealth-gradient-blue-light/50",
				alert: "bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-500 dark:hover:bg-yellow-600",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const expandableInfoBubbleIconVariants = cva("!size-5", {
	variants: {
		variant: {
			default: "text-stealth-border",
			stealth: "text-white",
			alert: "text-yellow-700 dark:text-white",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

type ExpandableInfoBubbleTriggerSide = "left" | "right";

export interface ExpandableInfoBubbleVariantProps
	extends VariantProps<typeof expandableInfoBubbleVariants> {}

interface ExpandableInfoBubbleProps extends ExpandableInfoBubbleVariantProps {
	children: React.ReactNode;
	className?: string;
	triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
	triggerSide?: ExpandableInfoBubbleTriggerSide;
}

function ExpandableInfoBubble({
	children,
	className,
	variant = "default",
	triggerProps,
	triggerSide = "left",
}: ExpandableInfoBubbleProps) {
	const isMdOrSmaller = useMediaQuery(`(max-width: ${breakpointsTailwind.md}px)`);
	const [isExpanded, setIsExpanded] = useState(isMdOrSmaller);
	const [hasMoreThanTwoLines, setHasMoreThanTwoLines] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const Icon = variant === "alert" ? TriangleAlert : InfoOutline;

	useEffect(() => {
		setIsExpanded(isMdOrSmaller);
	}, [isMdOrSmaller]);

	useLayoutEffect(() => {
		const content = contentRef.current;
		if (!isExpanded || !content) return;

		const measureContent = () => {
			const styles = getComputedStyle(content);
			const lineHeight = Number.parseFloat(styles.lineHeight);
			const textHeight =
				content.getBoundingClientRect().height -
				Number.parseFloat(styles.paddingTop) -
				Number.parseFloat(styles.paddingBottom);

			// Allow for fractional pixel rounding at the two-line boundary.
			setHasMoreThanTwoLines(textHeight > lineHeight * 2 + 1);
		};

		measureContent();
		const observer = new ResizeObserver(measureContent);
		observer.observe(content);
		return () => observer.disconnect();
	}, [isExpanded]);

	const trigger = (
		<button
			type="button"
			aria-expanded={isExpanded}
			className={cn(
				expandableInfoBubbleTriggerVariants({ variant }),
				isExpanded &&
					hasMoreThanTwoLines &&
					"bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent",
			)}
			onClick={() => setIsExpanded((current) => !current)}
			{...triggerProps}
		>
			<Icon aria-hidden="true" className={expandableInfoBubbleIconVariants({ variant })} />
		</button>
	);

	const content = (
		<RenderIf condition={isExpanded}>
			<div
				ref={contentRef}
				className={cn(
					"text-xs py-1 flex items-center px-2",
					triggerSide === "left" ? "pr-4" : "pl-4",
				)}
			>
				{children}
			</div>
		</RenderIf>
	);

	return (
		<div className={cn(expandableInfoBubbleVariants({ variant }), className)}>
			{triggerSide === "left" ? (
				<>
					{trigger}
					{content}
				</>
			) : (
				<>
					{content}
					{trigger}
				</>
			)}
		</div>
	);
}

export default memo(ExpandableInfoBubble);
